// External Imports
import { Fragment, useEffect, useRef, useState } from "react";
import { requestAdminAPI } from "../../api/posh-api";
import { MdSearch } from "react-icons/md";
import LoadingSpinner from "../UI/LoadingSpinner";

// Internal Imports

/** ========================================================================
 * * ApplicationSelector Component
 * Renders a dropdown search component, which allows selecting an option from a list.
 * ========================================================================== */

export default function ApplicationSelector({
  onSelect,
  selected,
  label = "APPLICATION",
}) {
  // Initialize States
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [show, setShow] = useState(false);

  /* ======================= FETCH VEHICLES ======================= */
  const fetchRecords = async (e) => {
    e.preventDefault();
    const query = e.target.query.value?.toLowerCase();
    if (!query) return;
    try {
      setLoading(true);
      setShow(false);
      const res = await requestAdminAPI("search", {
        table: "application",
        filters: { query, status: "active" },
      });
      setRecords(res);
      setShow(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH VEHICLE ======================= */
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "application",
        id: selected,
        columns: ["partner { name }"],
      });
      optionClickHandler(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Declare Refs
  const inputRef = useRef(null);
  const valueRef = useRef(null);

  /* ======================= NAME FORMATTER ======================= */
  const nameFormatter = (record) => {
    if (!record) return "";
    return `${record.year} ${record.model} ${record.vin?.substring(13, 17)} - ${
      record.partner.name
    }`;
  };

  /* ======================= OPTION CLICK HANDLER ======================= */
  const optionClickHandler = (record) => {
    inputRef.current.value = nameFormatter(record);
    valueRef.current.value = record.id;
    setShow(false);
    onSelect(record);
  };

  // If selection is made outside of the dropdown
  useEffect(() => {
    if (selected) fetchRecord();
  }, [selected]);

  return (
    <Fragment>
      {show && (
        <div
          onClick={() => setShow(false)}
          className="absolute top-0 left-0 w-full h-full -z-1 no-scrollbar"
        ></div>
      )}
      <div className="relative grid gap-2">
        <form onSubmit={fetchRecords} className="">
          <label className="text-sm text-stone-400">{label}:</label>
          <div className="flex items-center border rounded-md border-stone-600 ">
            <input
              className="relative w-full input"
              autoComplete="off"
              name="query"
              type="text"
              ref={inputRef}
              autoCorrect="off"
              spellCheck="false"
              placeholder="Type here and press enter to search..."
            />
            <button type="submit" className="px-2">
              {loading ? (
                <LoadingSpinner classes="text-primary w-6 h-6" />
              ) : (
                <MdSearch className="icon" />
              )}
            </button>
          </div>
        </form>
        <input className="hidden" name="vehicle" id="vehicle" ref={valueRef} />
        {show && (
          <div className="absolute top-16 py-2 px-4 w-full overflow-scroll rounded-lg bg-stone-700 no-scrollbar max-h-[300px] z-10">
            {records.map((vehicle) => (
              <div
                key={vehicle.id}
                className="px-2 py-1 text-sm font-semibold rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
                onClick={() => optionClickHandler(vehicle)}
              >
                {nameFormatter(vehicle)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Fragment>
  );
}
